import { h } from 'vue';

import {
  renderBoolean,
  renderMoney,
  translateEnum,
  translateNumberedEntity,
} from '@rygen/rygen-ui/i18n';
import {
  ColumnSpecification,
  Coordinates,
  Currency,
  Money,
  Optional,
  WeightUom,
} from '@rygen/rygen-ui/model';
import { notNull } from '@rygen/rygen-ui/util';
import { OrderShippingPoint as EsOrderShippingPoint } from '@rygen/tms-search-model/search/orderShippingPoint';

import i18n from '@/i18n';
import { buildSearchRoute, formatDateRange } from '@/util';

import OrderChip from '@/components/order/OrderChip.vue';
import ClientSearchResult from '@/components/search/ClientSearchResult.vue';
import ShippingPointSearchResult from '@/components/search/ShippingPointSearchResult.vue';
import AssignedUsersSearchResult from '@/components/user/AssignedUsersSearchResult.vue';
import TagContainer from '@rygen/rygen-ui/components/tag/TagContainer.vue';
import Chip from 'primevue/chip';

import { EmbeddableLocation } from '../EmbeddableLocation';
import { OrderShippingPoint } from '../order/OrderShippingPoint';
import { OrderStatus } from '../order/OrderStatus';
import { ShipmentStopType } from '../shipment/ShipmentStopType';

import {
  ES_DESTINATION_LOCATION_GROUP_COLUMN,
  ES_ORIGIN_LOCATION_GROUP_COLUMN,
} from './locationGroupColumn';
import { EsOrderColumnProps } from './OrderColumnProps';

export const convertEsOrderShippingPoint = (
  shippingPoint: Optional<EsOrderShippingPoint>,
  stopType: ShipmentStopType.PICKUP | ShipmentStopType.DROP_OFF,
) => {
  return shippingPoint
    ? new OrderShippingPoint({
        id: shippingPoint.id,
        stopType,
        location: new EmbeddableLocation({
          locationId: shippingPoint.location?.ficonLocationId,
          locationName: shippingPoint.location?.locationName,
          code: shippingPoint.location?.code,
          addressLine1: shippingPoint.location?.addressLine1,
          addressLine2: shippingPoint.location?.addressLine2,
          addressLine3: shippingPoint.location?.addressLine3,
          city: shippingPoint.location?.city,
          state: shippingPoint.location?.state,
          postalCode: shippingPoint.location?.postalCode,
          country: shippingPoint.location?.country,
          coordinates: new Coordinates({
            latitude: shippingPoint.location?.coordinates?.lat,
            longitude: shippingPoint.location?.coordinates?.lon,
          }),
          timeZone: shippingPoint.location?.timeZone,
        }),
      })
    : undefined;
};

const tagSearchRouteBuilder = (tag: string) => {
  return buildSearchRoute('orderList', { tags: [tag] });
};

const OWNING_CLIENT_COLUMN = new ColumnSpecification({
  field: 'client',
  header: i18n.global.t('clients.clients'),
  body: ({ data }: EsOrderColumnProps) =>
    h(ClientSearchResult, { domainId: data.owner?.id }),
});

const STATUS_COLUMN = new ColumnSpecification({
  field: 'status',
  header: i18n.global.t('common.status'),
  body: ({ data }: EsOrderColumnProps) =>
    h(OrderChip, { status: data.status as OrderStatus }),
});

const TAG_COLUMN = new ColumnSpecification({
  field: 'tags',
  header: i18n.global.t('tags.tags', 2),
  class: 'tags-column',
  body: ({ data }: EsOrderColumnProps) =>
    h(TagContainer, {
      class: 'h-auto',
      modelValue: data.tags ?? [],
      routeBuilder: tagSearchRouteBuilder,
      readonly: true,
      maxVisible: 2,
    }),
});

const ORIGIN_COLUMN = new ColumnSpecification({
  field: 'origin',
  header: i18n.global.t('orders.origin'),
  body: ({ data }: EsOrderColumnProps) => {
    // TODO: Convert ES search result to our concrete classes before returning results
    const shippingPoint = convertEsOrderShippingPoint(
      data.origin,
      ShipmentStopType.PICKUP,
    );
    return h(ShippingPointSearchResult, { shippingPoint });
  },
});

const DESTINATION_COLUMN = new ColumnSpecification({
  field: 'destination',
  header: i18n.global.t('orders.destination'),
  body: ({ data }: EsOrderColumnProps) => {
    // TODO: Convert ES search result to our concrete classes before returning results
    const shippingPoint = convertEsOrderShippingPoint(
      data.destination,
      ShipmentStopType.DROP_OFF,
    );
    return h(ShippingPointSearchResult, { shippingPoint });
  },
});

const REQUESTED_PICKUP_DATE_RANGE_COLUMN = new ColumnSpecification({
  field: 'requestedPickupDateRange',
  header: i18n.global.t('orders.requestedPickup'),
  body: ({ data }: EsOrderColumnProps) =>
    formatDateRange(
      data.origin?.requestedAppointmentRange,
      data.origin?.location?.timeZone,
    ),
});

const PICKUP_APPOINTMENT_REQUIRED_COLUMN = new ColumnSpecification({
  field: 'pickupAppointmentRequired',
  header: i18n.global.t('shippingPoints.pickupAppointmentRequired'),
  body: ({ data }: EsOrderColumnProps) =>
    renderBoolean(data.origin?.appointmentRequired ?? false),
});

const REQUESTED_DELIVERY_DATE_RANGE_COLUMN = new ColumnSpecification({
  field: 'requestedDeliveryDateRange',
  header: i18n.global.t('orders.requestedDelivery'),
  body: ({ data }: EsOrderColumnProps) =>
    formatDateRange(
      data.destination?.requestedAppointmentRange,
      data.destination?.location?.timeZone,
    ),
});

const DELIVERY_APPOINTMENT_REQUIRED_COLUMN = new ColumnSpecification({
  field: 'deliveryAppointmentRequired',
  header: i18n.global.t('shippingPoints.deliveryAppointmentRequired'),
  body: ({ data }: EsOrderColumnProps) =>
    renderBoolean(data.destination?.appointmentRequired ?? false),
});

const WEIGHT_COLUMN = new ColumnSpecification({
  field: 'weight',
  header: i18n.global.t('lineItems.weight'),
  body: ({ data }: EsOrderColumnProps) => {
    return data.lineItemSummary?.totalWeight?.value == null
      ? '-'
      : translateNumberedEntity(
          data.lineItemSummary.totalWeight.value,
          `uoms.weight.abbreviations.${data.lineItemSummary?.totalWeight?.unit ?? WeightUom.POUND}`,
        );
  },
});

const HANDLING_UNIT_COUNT = new ColumnSpecification({
  field: 'handlingUnitCount',
  header: i18n.global.t('lineItems.handlingUnits'),
  body: ({ data }: EsOrderColumnProps) => {
    const qty = data.lineItemSummary?.totalHandlingQuantity;
    const uom =
      data.lineItemSummary?.handlingUnit?.name ??
      i18n.global.t('lineItems.handlingUnits');
    return qty == null ? '-' : `${qty} ${uom}`;
  },
});

const PRODUCT_NUMBER_COLUMN = new ColumnSpecification({
  field: 'productNumber',
  header: i18n.global.t('lineItems.productNumber'),
  body: ({ data }: EsOrderColumnProps) =>
    data.lineItemSummary?.productNumbers ?? '-',
});

const PRODUCT_DESCRIPTION_COLUMN = new ColumnSpecification({
  field: 'productDescription',
  header: i18n.global.t('lineItems.productDescription'),
  body: ({ data }: EsOrderColumnProps) =>
    data.lineItemSummary?.productDescriptions ?? '-',
});

const HAZMAT_COLUMN = new ColumnSpecification({
  field: 'hazmat',
  header: i18n.global.t('lineItems.hazmat'),
  body: ({ data }: EsOrderColumnProps) => {
    const lineItems = data.lineItems ?? [];
    const isHazmat = lineItems.some((l) => l.hazmatData?.hazmat);
    return renderBoolean(isHazmat);
  },
});

const GL_CODE_COLUMN = new ColumnSpecification({
  field: 'glCodes',
  header: i18n.global.t('glCodes.glCodes', 2),
  class: 'gl-codes-column',
  body: ({ data }: EsOrderColumnProps) => {
    if (!data.glCodes?.length) {
      return '-';
    }

    const glCodes = data.glCodes.map((g) => h(Chip, { label: g }));
    return h('div', { class: 'flex flex-gap-1' }, glCodes);
  },
});

const DIRECTION_COLUMN = new ColumnSpecification({
  field: 'direction',
  header: i18n.global.t('shipments.directions'),
  body: ({ data }: EsOrderColumnProps) =>
    data.direction ? translateEnum(data.direction, 'Direction') : '-',
});

const EQUIPMENT_TYPE_COLUMN = new ColumnSpecification({
  field: 'equipmentType',
  header: i18n.global.t('carriers.equipmentTypes'),
  body: ({ data }: EsOrderColumnProps) => data.equipmentType?.name ?? '-',
});

const FREIGHT_ALLOWANCE_ENABLED_COLUMN = new ColumnSpecification({
  field: 'freightAllowanceEnabled',
  header: i18n.global.t('orders.freightAllowance.freightAllowanceEnabled'),
  body: ({ data }: EsOrderColumnProps) =>
    renderBoolean(data.freightAllowance?.enabled ?? false),
});

const FREIGHT_ALLOWANCE_AMOUNT_COLUMN = new ColumnSpecification({
  field: 'freightAllowance',
  header: i18n.global.t('orders.freightAllowance.freightAllowance'),
  body: ({ data }: EsOrderColumnProps) => {
    // TODO: Convert ES search result to our concrete classes before returning results
    const freightAllowance = data.freightAllowance?.amount
      ? new Money({
          amount: data.freightAllowance.amount.amount,
          currency: data.freightAllowance.amount.currency as Currency,
        })
      : undefined;
    return renderMoney(freightAllowance);
  },
});

const SPOT_MARKET_SUMMARY_COLUMN = new ColumnSpecification({
  field: 'spotMarketRequestCount',
  header: i18n.global.t('carrierPortal.spotMarket.spotMarkets', 2),
  body: ({ data }: EsOrderColumnProps) => {
    const spotMarketCountText = i18n.global.t(
      'carrierPortal.spotMarket.countSpotMarketRequestsSent',
      { count: data.spotMarketSummary?.requested ?? 0 },
    );

    if (!data.spotMarketSummary?.requested) {
      return spotMarketCountText;
    }

    const requestEl = h('div', {}, spotMarketCountText);
    const acceptedEl = h(
      'div',
      { class: 'text-gray-darker' },
      i18n.global.t(
        'carrierPortal.spotMarket.countSpotMarketRequestsAccepted',
        { count: data.spotMarketSummary.accepted ?? 0 },
      ),
    );
    return h('div', {}, [requestEl, acceptedEl]);
  },
});

const USER_ASSIGNMENT_COLUMN = new ColumnSpecification({
  field: 'assignedUsers',
  header: i18n.global.t('common.assignedParam', {
    param: i18n.global.t('users.users', 2),
  }),
  body: ({ data }: EsOrderColumnProps) =>
    h(AssignedUsersSearchResult, {
      id: 'assign-user-button',
      modelValue: data.assignedUsers || [],
      parentId: data.id,
      ownerId: data.owner?.id,
      type: 'order',
    }),
});

const CANCELLATION_REASON_COLUMN = new ColumnSpecification({
  field: 'cancellationReason',
  header: i18n.global.t('orders.cancellationReason'),
  body: ({ data }: EsOrderColumnProps) => data.cancellationReason ?? '-',
});

const REQUESTED_MODE_COLUMN = new ColumnSpecification({
  field: 'requestedMode',
  header: i18n.global.t('common.requestedMode'),
  body: ({ data }: EsOrderColumnProps) =>
    data.requestedMode ? translateEnum(data.requestedMode, 'Mode') : '-',
});

const SOURCE_COLUMN = new ColumnSpecification({
  field: 'source',
  header: i18n.global.t('common.source'),
  body: ({ data }: EsOrderColumnProps) => data.source ?? '-',
});

const SWC_COLUMN = new ColumnSpecification({
  field: 'swc',
  header: i18n.global.t('swc.shipWithoutContact'),
  body: ({ data }: EsOrderColumnProps) => renderBoolean(data.swc ?? false),
});

const CUSTOMER_ROUTED_COLUMN = new ColumnSpecification({
  field: 'customerRouted',
  header: i18n.global.t('common.customerRouted'),
  body: ({ data }: EsOrderColumnProps) =>
    renderBoolean(data.customerRouted ?? false),
});

const HOT_COLUMN = new ColumnSpecification({
  field: 'hot',
  header: i18n.global.t('common.hot'),
  body: ({ data }: EsOrderColumnProps) => renderBoolean(data.hot ?? false),
});

const MILEAGE_COLUMN = new ColumnSpecification({
  field: 'totalMileage',
  header: i18n.global.t('common.mileage'),
  body: ({ data }: EsOrderColumnProps): string =>
    data.totalMileage
      ? i18n.global.t('rateShop.mileage', {
          count: Math.round(data.totalMileage),
        })
      : '-',
});

/**
 * All possible order columns
 *
 * Order is significant. This array is used as the default set of columns.
 **/
export const ES_ORDER_COLUMNS: ColumnSpecification[] = [
  OWNING_CLIENT_COLUMN,
  STATUS_COLUMN,
  USER_ASSIGNMENT_COLUMN,
  TAG_COLUMN,
  ORIGIN_COLUMN,
  DESTINATION_COLUMN,
  REQUESTED_PICKUP_DATE_RANGE_COLUMN,
  PICKUP_APPOINTMENT_REQUIRED_COLUMN,
  REQUESTED_DELIVERY_DATE_RANGE_COLUMN,
  DELIVERY_APPOINTMENT_REQUIRED_COLUMN,
  GL_CODE_COLUMN,
  WEIGHT_COLUMN,
  HANDLING_UNIT_COUNT,
  PRODUCT_NUMBER_COLUMN,
  PRODUCT_DESCRIPTION_COLUMN,
  HAZMAT_COLUMN,
  DIRECTION_COLUMN,
  EQUIPMENT_TYPE_COLUMN,
  FREIGHT_ALLOWANCE_AMOUNT_COLUMN,
  FREIGHT_ALLOWANCE_ENABLED_COLUMN,
  SPOT_MARKET_SUMMARY_COLUMN,
  CANCELLATION_REASON_COLUMN,
  REQUESTED_MODE_COLUMN,
  SOURCE_COLUMN,
  SWC_COLUMN,
  CUSTOMER_ROUTED_COLUMN,
  HOT_COLUMN,
  MILEAGE_COLUMN,
  ES_ORIGIN_LOCATION_GROUP_COLUMN,
  ES_DESTINATION_LOCATION_GROUP_COLUMN,
];

export const ES_ORDER_COLUMNS_SORT_FIELDS = ES_ORDER_COLUMNS.map(
  (col) => col.field,
)
  .filter(notNull)
  .concat('id', '');
